import { Button, Drawer, Empty, Input, Skeleton, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import React, { useEffect, useState } from 'react';

import { useBrowseAssets, type KnowledgeCardResponseDTO } from '@/hooks/useBrowseAssets';
import { getTypeBadgeForDTO } from '@/models/knowledge-card-badge';

const { Text } = Typography;
const { Search } = Input;

/** Props for the `BrowseAssetsDrawer` slide-in panel. */
export interface BrowseAssetsDrawerProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  projectId: string;
  accessToken: string;
  /** Called when a card is selected — links it to the assessment. */
  onSelect: (cardId: string) => void;
  /** IDs already linked so we can show a visual indicator. */
  linkedCardIds?: string[];
}

/** Builds the Ant Design table column definitions for the asset browse list, marking already-linked cards. */
const browseColumns = (
  linkedCardIds: string[],
  onSelect: (id: string) => void,
): NonNullable<TableProps<KnowledgeCardResponseDTO>['columns']> => [
  {
    title: 'Type',
    key: 'type',
    width: 100,
    render: (_: unknown, record: KnowledgeCardResponseDTO) => {
      const badge = getTypeBadgeForDTO(record.source_type, record.source_url);
      return (
        <Tag color={badge.color} style={{ margin: 0, fontSize: 11 }}>
          {badge.label}
        </Tag>
      );
    },
  },
  {
    title: 'Asset Name',
    key: 'title',
    render: (_: unknown, record: KnowledgeCardResponseDTO) => (
      <Text ellipsis={{ tooltip: record.title }}>{record.title}</Text>
    ),
  },
  {
    title: '',
    key: 'action',
    width: 72,
    align: 'right' as const,
    render: (_: unknown, record: KnowledgeCardResponseDTO) => {
      const isLinked = linkedCardIds.includes(record.card_id);
      return isLinked ? (
        <Tag color="success" style={{ margin: 0, fontSize: 11 }}>
          Linked
        </Tag>
      ) : (
        <Button size="small" type="primary" onClick={() => onSelect(record.card_id)}>
          Link
        </Button>
      );
    },
  },
];

/** Right-side drawer for searching and linking existing project knowledge cards to an assessment. */
export const BrowseAssetsDrawer: React.FC<BrowseAssetsDrawerProps> = ({
  open,
  onClose,
  clientId,
  projectId,
  accessToken,
  onSelect,
  linkedCardIds = [],
}) => {
  const [query, setQuery] = useState('');
  const { cards, loading, fetchCards } = useBrowseAssets(open, accessToken, clientId, projectId);

  // Reset the search input each time the drawer opens
  useEffect(() => {
    if (open) {
      setQuery('');
    }
  }, [open]);

  /** Applies a new search query and re-fetches the card list. */
  const handleSearch = (value: string): void => {
    setQuery(value);
    void fetchCards(value);
  };

  const columns = browseColumns(linkedCardIds, onSelect);

  return (
    <Drawer
      title="Browse Existing Assets"
      placement="right"
      width={520}
      open={open}
      onClose={onClose}
      styles={{ body: { padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 } }}
    >
      <Search
        placeholder="Search assets…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onSearch={handleSearch}
        allowClear
      />

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <Table<KnowledgeCardResponseDTO>
          rowKey="card_id"
          size="small"
          dataSource={cards}
          columns={columns}
          pagination={false}
          locale={{ emptyText: <Empty description="No assets found" /> }}
          rowClassName={(record) =>
            linkedCardIds.includes(record.card_id) ? 'browse-assets-row--linked' : ''
          }
        />
      )}
    </Drawer>
  );
};

export default BrowseAssetsDrawer;
